import * as THREE from 'three';
import { useMemo, useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import { useGameStore } from '../stores/gameStore';

export default function ProceduralConstellations({ startX = 400, endX = 563, count = 35 }) {
    const groupRef = useRef();
    const phase = useGameStore(state => state.phase);

    // Carreguem la partícula específica!
    const particleTexture = useTexture('/particles/1 copy.png');

    const { edgesData, pointsGeom, totalEdges } = useMemo(() => {
        const edges = [];
        const pointPositions = [];

        for (let i = 0; i < count; i++) {
            // Punt central de la constel·lació a l'espai 3D (dins la zona 400-563)
            const cx = startX + Math.random() * (endX - startX);

            // Augmentem brutalment la dispersió per separar-les i allunyar-les del centre
            const angle = Math.random() * Math.PI * 2;
            const radius = 25 + Math.random() * 250;

            const cy = 2 + Math.sin(angle) * radius;
            const cz = Math.cos(angle) * radius;

            // Quants nodes / estrelles tindrà aquesta constel·lació local
            const numStars = 4 + Math.floor(Math.random() * 6);
            const stars = [];

            for (let j = 0; j < numStars; j++) {
                // Offset de cada estrella respecte el centre de la seva constel·lació
                const px = cx + (Math.random() - 0.5) * 15;
                const py = cy + (Math.random() - 0.5) * 15;
                const pz = cz + (Math.random() - 0.5) * 15;
                stars.push(new THREE.Vector3(px, py, pz));

                pointPositions.push(px, py, pz);
            }

            // Helper d'Instanciació per crear Línies Gruixudes (Cilindres)
            const addEdge = (p1, p2) => {
                const distance = p1.distanceTo(p2);
                const midpoint = p1.clone().lerp(p2, 0.5);
                const direction = p2.clone().sub(p1).normalize();
                const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
                const euler = new THREE.Euler().setFromQuaternion(quaternion);
                edges.push({ position: midpoint, rotation: euler, length: distance });
            };

            // Connectar els punts entre ells de forma seqüencial
            for (let j = 0; j < numStars - 1; j++) {
                addEdge(stars[j], stars[j + 1]);
            }

            // Afegir unes connexions creuades "Random" a la mateixa constel·lació
            const extraLines = Math.floor(numStars / 2);
            for (let r = 0; r < extraLines; r++) {
                const a = Math.floor(Math.random() * numStars);
                let b = Math.floor(Math.random() * numStars);
                while (a === b) b = Math.floor(Math.random() * numStars);

                if (stars[a].distanceTo(stars[b]) < 12) {
                    addEdge(stars[a], stars[b]);
                }
            }
        }

        const pGeom = new THREE.BufferGeometry();
        pGeom.setAttribute('position', new THREE.Float32BufferAttribute(pointPositions, 3));

        return { edgesData: edges, pointsGeom: pGeom, totalEdges: edges.length };
    }, [startX, endX, count]);

    // Instanciar Matrix per al gruix a les línies
    const linesInstancedRef = useRef();
    const lineGeo = useMemo(() => new THREE.CylinderGeometry(0.08, 0.08, 1, 5), []); // 0.08 dóna un gruix elegant

    useEffect(() => {
        if (!linesInstancedRef.current || !edgesData.length) return;
        const dummy = new THREE.Object3D();
        for (let i = 0; i < edgesData.length; i++) {
            dummy.position.copy(edgesData[i].position);
            dummy.rotation.copy(edgesData[i].rotation);
            dummy.scale.set(1, edgesData[i].length, 1);
            dummy.updateMatrix();
            linesInstancedRef.current.setMatrixAt(i, dummy.matrix);
        }
        linesInstancedRef.current.instanceMatrix.needsUpdate = true;
    }, [edgesData]);

    useFrame((state, delta) => {
        if (!groupRef.current) return;

        // Lleuger moviment respiratori zen de tot el cinturó de constel·lacions
        groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.5;

        // ACCELERACIÓ IDÈNTICA A SPEEDLINES I PARTÍCULES!
        if (phase === 'playing' || phase === 'launching') {
            const camX = state.camera.position.x;
            let globalSpeedMultiplier = 0.5; // Base igual que particules

            if (camX >= 380) {
                const progress = THREE.MathUtils.clamp((camX - 380) / (563 - 380), 0, 1);
                // Ajust suau cap un punt intermedi d'acceleració
                const smoothExp = Math.pow(progress, 4);
                const hyperExp = Math.pow(progress, 12);
                globalSpeedMultiplier = 0.5 + (smoothExp * 30.0) + (hyperExp * 170.0);
            }

            // Movem EL GRUP SENCER cap a les X negatives.
            // La velocitat pròpia base és petita per donar sensació còsmica lleugera, però escalada brutalment amb el drop
            groupRef.current.position.x -= delta * globalSpeedMultiplier * 10;
        }
    });

    return (
        <group ref={groupRef} visible={phase === 'playing' || phase === 'launching' || phase === 'ready'}>
            {/* Draw Call 1: Línies GRUIXUDES (Cilindres Instanciats per guanyar FPS però tenir gruix) */}
            <instancedMesh ref={linesInstancedRef} args={[lineGeo, null, totalEdges]}>
                <meshBasicMaterial
                    color="#0088ff"
                    transparent
                    opacity={0.4}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </instancedMesh>

            {/* Draw Call 2: Punts Blaus Base am la textura 1 copy.png */}
            <points geometry={pointsGeom}>
                <pointsMaterial
                    map={particleTexture}
                    alphaMap={particleTexture}
                    color="#00ffff"
                    size={4.0}
                    transparent
                    opacity={0.9}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                    sizeAttenuation={true}
                />
            </points>

            {/* Draw Call 3: Aura Gran Blava Brillants (Mateixa textura, menys opaca i més gran) */}
            <points geometry={pointsGeom}>
                <pointsMaterial
                    map={particleTexture}
                    alphaMap={particleTexture}
                    color="#ffffffff"
                    size={8.5}
                    transparent
                    opacity={0.3}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                    sizeAttenuation={true}
                />
            </points>
        </group>
    );
}
